import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { Grid, FormControlLabel } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'
import SydSwitch from '../../../../../commonDesign/SydSwitch'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1)
  },
  infoText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  formControlLabel: {
    '& .MuiFormControlLabel-label': {
      paddingLeft: 'var(--padding-sm)'
    }
  }
}))

const useSubmitter = (dataSource, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: modifyDatasourceAiEnabled } = dataCatalog.useModifyDatasourceAiEnabled()

  const onSubmit = useCallback(async (formData) => {
    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      isAiEnabled: formData.isAiEnabled
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await modifyDatasourceAiEnabled(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the AI enabled status')
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, onComplete, modifyDatasourceAiEnabled])

  return {
    onSubmit,
    processing,
    error
  }
}

function ModifyDatasourceAiEnabledForm ({ dataSource, onCancel, onComplete }) {
  const classes = useStyles()
  const [isAiEnabled, setIsAiEnabled] = useState(dataSource.isAiEnabled || false)
  const { onSubmit, processing, error } = useSubmitter(dataSource, onComplete)

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No data source provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.infoText}>
            Control whether this datasource is available for AI-powered features.
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.switchContainer}>
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <SydSwitch
                  value={isAiEnabled}
                  onChange={(e, newValue) => setIsAiEnabled(newValue)}
                  disabled={processing}
                />
              }
              label={isAiEnabled ? 'AI Enabled' : 'AI Disabled'}
            />
          </div>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <SydModalActions>
            {error && <div className='__error'>{error}</div>}
            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={() => onSubmit({ isAiEnabled })}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

ModifyDatasourceAiEnabledForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    isAiEnabled: PropTypes.bool
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default ModifyDatasourceAiEnabledForm
